import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Alert, Box, IconButton } from "@mui/material";
import { lighten } from "@mui/system";
import { useTheme } from "@mui/material/styles";
import { ConfirmationModal } from "@packsize/components";
import { useToast } from "@packsize/toast";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import { deleteSelectionPipeline } from "@/api/selection-api";
import { emptyGuid } from "@/constants";
import { useDeleteProductionGroup } from "@/hooks/production-groups";
import type { ProductionGroup } from "@/schemas/production-group";

interface ProductionGroupDeleteButtonProps {
	readonly productionGroup: ProductionGroup;
}

export const ProductionGroupDeleteButton: React.FC<ProductionGroupDeleteButtonProps> = ({ productionGroup }) => {
	const intl = useIntl();
	const theme = useTheme();
	const { toastSuccess, toastError } = useToast();

	const [showDeleteConfirmation, setShowDeleteConfirmation] = React.useState(false);
	const [isDisabled, setIsDisabled] = React.useState(false);
	const [showOpenWorkError, setShowOpenWorkError] = React.useState(false);

	const { mutate: deleteProductionGroup, isPending } = useDeleteProductionGroup();

	const handleDeleteConfirmed = (): void => {
		deleteProductionGroup(productionGroup.id, {
			onSuccess: async (result) => {
				if (!result.deleted) {
					setShowOpenWorkError(true);
					setIsDisabled(true);
					return;
				}

				try {
					if (productionGroup.pipelineId && productionGroup.pipelineId !== emptyGuid) {
						await deleteSelectionPipeline(productionGroup.pipelineId);
					}
				} catch {
					/* Best-effort cleanup: the production group is already deleted, don't block on pipeline cleanup. */
				}

				setShowDeleteConfirmation(false);
				setShowOpenWorkError(false);
				setIsDisabled(false);

				toastSuccess({
					message: intl.formatMessage({
						id: "Production group deleted successfully",
						defaultMessage: "Production group deleted successfully",
					}),
				});
			},
			onError: () => {
				toastError({
					message: intl.formatMessage({
						id: "Failed to delete production group",
						defaultMessage: "Failed to delete production group",
					}),
				});

				setShowDeleteConfirmation(false);
				setShowOpenWorkError(false);
				setIsDisabled(false);
			},
		}).catch(() => {
			/* Handled via onError above; caught here to avoid an unhandled rejection since mutate is mutateAsync. */
		});
	};

	const handleClose = (): void => {
		setShowDeleteConfirmation(false);
		setShowOpenWorkError(false);
		setIsDisabled(false);
	};

	return (
		<>
			<ConfirmationModal
				open={showDeleteConfirmation}
				onClose={handleClose}
				onCancel={handleClose}
				onConfirm={handleDeleteConfirmed}
				title={intl.formatMessage({ id: "Delete production group", defaultMessage: "Delete production group" })}
				confirmText={intl.formatMessage({ id: "Delete", defaultMessage: "Delete" })}
				cancelText={intl.formatMessage({ id: "Cancel", defaultMessage: "Cancel" })}
				slotProps={{
					confirmButton: {
						disabled: isDisabled,
						loading: isPending,
						"data-testid": "production-group-delete-confirmation-modal__button__confirm",
					},
					cancelButton: {
						"data-testid": "production-group-delete-confirmation-modal__button__cancel",
					},
				}}
			>
				<Box>
					{intl.formatMessage({
						id: "This change can not be undone.",
						defaultMessage: "This change can not be undone.",
					})}
				</Box>
				{showOpenWorkError === true && (
					<Box marginTop={2}>
						<Alert severity="error" sx={{ backgroundColor: lighten(theme.palette.error.main, 0.9) }}>
							<FormattedMessage id="Before you can delete" defaultMessage="Before you can delete" />
							<ul>
								<li>
									<FormattedMessage
										id="There must not be pending work assigned to {itemName}"
										defaultMessage="There must not be pending work assigned to {itemName}"
										values={{ itemName: productionGroup.alias }}
									/>
								</li>
							</ul>
						</Alert>
					</Box>
				)}
			</ConfirmationModal>
			<IconButton
				onClick={() => setShowDeleteConfirmation(true)}
				data-testid="production-group-delete-button__button__open-modal"
				data-cy="delete-button"
				data-pendoid="production-groups__button__delete"
			>
				<DeleteOutlineIcon sx={{ fontSize: 24, color: "text.primary" }} />
			</IconButton>
		</>
	);
};
